import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, unref as _unref, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-col h-full justify-between" }
const _hoisted_2 = { class: "space-y-6" }
const _hoisted_3 = { class: "grid grid-cols-3 gap-4" }
const _hoisted_4 = { class: "bg-white p-4 rounded-lg shadow-sm border border-gray-200" }
const _hoisted_5 = { class: "text-2xl font-bold text-gray-800" }
const _hoisted_6 = { class: "bg-white p-4 rounded-lg shadow-sm border border-gray-200" }
const _hoisted_7 = { class: "text-2xl font-bold text-gray-800" }
const _hoisted_8 = { class: "bg-white p-4 rounded-lg shadow-sm border border-gray-200" }
const _hoisted_9 = { class: "text-2xl font-bold text-gray-800" }
const _hoisted_10 = { class: "bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" }
const _hoisted_11 = { class: "flex overflow-x-auto border-b border-gray-200" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "flex flex-col items-start gap-1" }
const _hoisted_14 = { class: "text-xs font-semibold" }
const _hoisted_15 = { class: "flex flex-wrap gap-1" }
const _hoisted_16 = {
  key: 0,
  class: "text-gray-400"
}
const _hoisted_17 = {
  key: 0,
  class: "h-6 w-px bg-gray-300 self-center"
}
const _hoisted_18 = { class: "overflow-x-auto" }
const _hoisted_19 = {
  key: 0,
  class: "text-center py-8 text-gray-500"
}
const _hoisted_20 = {
  key: 1,
  class: "min-w-full"
}
const _hoisted_21 = { class: "divide-y divide-gray-200" }
const _hoisted_22 = { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900" }
const _hoisted_23 = { class: "px-6 py-4 text-sm text-gray-500 max-w-xs" }
const _hoisted_24 = { class: "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" }
const _hoisted_25 = { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900" }
const _hoisted_26 = { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900" }
const _hoisted_27 = ["onClick"]
const _hoisted_28 = { class: "w-full text-center" }
const _hoisted_29 = ["disabled"]

import { computed, ref, watch } from 'vue';
import { VaccinationProgressEntry } from '@/types/v2';
import { useRouter } from 'vue-router';
import { useVaccination } from "@/composables/tasks/vaccination/useVaccination";
import { useVaccinationConfigurations } from "@/composables/tasks/vaccination/useVaccinationConfigurations";
import { useRecord } from "@/composables/tasks/vaccination/useVaccinationRecords";

import FooterActions from '@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue';
import VaccinationRevisionModal from "@/components/v2/Tasks/Vaccination/Modals/VaccinationRevisionModal.vue";
import { useWeight } from "@/composables/tasks/vaccination/useVaccinationWeight";
import { useTask } from "@/composables/tasks/useTask";
import { TasksService } from "@/services/v2/tasks";
import { useNotifications } from "@/composables/useNotifications";
import { useEquipment } from "@/composables/tasks/useEquipment";
import TaskCompletionBlockedModal from "@/components/v2/Tasks/Task/TaskCompletionBlockedModal.vue";

// ===================================================================
// Composables
// ===================================================================

export default /*@__PURE__*/_defineComponent({
  __name: 'OverviewView',
  setup(__props) {

const router = useRouter();
const { activeTask } = useTask();
const { configurations } = useVaccination();
const { getConfigurationTags } = useVaccinationConfigurations();
const { getVaccinationRecordsByConfigKey } = useRecord();
const { getAverageWeightByConfigKeyVaccinationRecords } = useWeight();
const { showTaskCompletionSuccess, showError } = useNotifications();
const { getWeightIdCompletionValidation } = useEquipment();

// ===================================================================
// State
// ===================================================================
const activeConfigKey = ref<string>('');
const vaccinationRevisionModal = ref<boolean>(false);
const selectedEntry = ref<VaccinationProgressEntry | null>(null);
const selectedEntryIndex = ref<number>(-1);
const isFinishing = ref<boolean>(false);
const completionBlockModal = ref<boolean>(false);

// ===================================================================
// Actions
// ===================================================================
// Go back
const goBack = () => router.go(-1);

// Open revisions modal
const openRevisionsModal = (entry: VaccinationProgressEntry, entryIndex: number) => {
  vaccinationRevisionModal.value = true;
  selectedEntry.value = entry;
  selectedEntryIndex.value = entryIndex;
}

// Finish task handler
const handleFinishTask = async () => {
    if (!activeTask.value) return;

    const validation = getWeightIdCompletionValidation();
    if (!validation.isValid) {
      completionBlockModal.value = true;
      return;
    }
    
    isFinishing.value = true;
    try {
        await TasksService.finishTask(activeTask.value);
        showTaskCompletionSuccess(activeTask.value.title);
    } catch (error) {
        console.error('Error completing vaccination task:', error);
        showError("Task Completion Failed", "Failed to complete the vaccination task. Please try again.");
    } finally {
        isFinishing.value = false;
    }
}

// ===================================================================
// Computed
// ===================================================================
const vaccinationRecords = computed<VaccinationProgressEntry[] | []>(() => getVaccinationRecordsByConfigKey(activeConfigKey.value));
const averageWeight = computed<number | string>(() => getAverageWeightByConfigKeyVaccinationRecords(activeConfigKey.value));
const completionValidation = computed(() => getWeightIdCompletionValidation());
const getDestinationTankName = (config: any): string => {
  const destinationTankName = config?.destinationTank?.name || config?.destinationTank?.label;
  if (destinationTankName) return destinationTankName;
  return config?.tank?.name || config?.tank?.label || 'No destination tank';
};

// ===================================================================
// Watchers
// ===================================================================
// Watch for configurations composable state change
watch(
    () => configurations.value,
    (newConfigurations) => {
      if (!activeConfigKey.value && newConfigurations.length > 0) {
        activeConfigKey.value = newConfigurations[0].key;
      }
    },
    { immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", { class: "flex justify-between items-center" }, [
        _cache[3] || (_cache[3] = _createElementVNode("h1", { class: "text-2xl font-bold text-gray-800" }, "IVP Administration Records", -1)),
        _createElementVNode("button", {
          onClick: goBack,
          class: "text-gray-600 hover:text-gray-800 flex items-center gap-1 text-sm"
        }, _cache[2] || (_cache[2] = [
          _createElementVNode("svg", {
            xmlns: "http://www.w3.org/2000/svg",
            class: "h-4 w-4",
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            "stroke-width": "1.2",
            "stroke-linecap": "round",
            "stroke-linejoin": "round"
          }, [
            _createElementVNode("path", { d: "M19 12H5M12 19l-7-7 7-7" })
          ], -1),
          _createTextVNode(" Back ")
        ]))
      ]),
      _cache[8] || (_cache[8] = _createElementVNode("span", { class: "w-full bg-secondary h-[1px] block" }, null, -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _cache[4] || (_cache[4] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-500 mb-1" }, "Total Administered IVPs", -1)),
          _createElementVNode("p", _hoisted_5, _toDisplayString(vaccinationRecords.value.reduce((sum, record) => sum + (record.vaccinated || 0), 0)), 1)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _cache[5] || (_cache[5] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-500 mb-1" }, [
            _createTextVNode("Average Weight "),
            _createElementVNode("span", { class: "text-xs" }, "(grams)")
          ], -1)),
          _createElementVNode("p", _hoisted_7, _toDisplayString(averageWeight.value), 1)
        ]),
        _createElementVNode("div", _hoisted_8, [
          _cache[6] || (_cache[6] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-500 mb-1" }, "Total Records", -1)),
          _createElementVNode("p", _hoisted_9, _toDisplayString(vaccinationRecords.value.length), 1)
        ])
      ]),
      _createElementVNode("div", _hoisted_10, [
        _createElementVNode("div", _hoisted_11, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(configurations), (config, index) => {
            return (_openBlock(), _createElementBlock(_Fragment, {
              key: config.key
            }, [
              _createElementVNode("button", {
                onClick: ($event: any) => (activeConfigKey.value = config.key),
                class: _normalizeClass(["px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-1", activeConfigKey.value === config.key
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'])
              }, [
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("span", _hoisted_14, _toDisplayString(getDestinationTankName(config)), 1),
                  _createElementVNode("div", _hoisted_15, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getConfigurationTags)(config), (tag) => {
                      return (_openBlock(), _createElementBlock("span", {
                        key: tag.key,
                        class: "inline-block px-2 py-0.5 text-xs font-medium rounded-full",
                        style: _normalizeStyle({
                      backgroundColor: tag.backgroundColor || '#e5e7eb',
                      color: tag.textColor || '#374151'
                    })
                      }, _toDisplayString(tag.title), 5))
                    }), 128)),
                    (_unref(getConfigurationTags)(config).length === 0)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_16, "No tags"))
                      : _createCommentVNode("", true)
                  ])
                ])
              ], 10, _hoisted_12),
              (index < _unref(configurations).length - 1)
                ? (_openBlock(), _createElementBlock("div", _hoisted_17))
                : _createCommentVNode("", true)
            ], 64))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_18, [
          (!vaccinationRecords.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_19, " No vaccination records for this configuration "))
            : (_openBlock(), _createElementBlock("table", _hoisted_20, [
                _cache[7] || (_cache[7] = _createElementVNode("thead", { class: "bg-gray-50" }, [
                  _createElementVNode("tr", null, [
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " # "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " Comment "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " Administered IVPs "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " Weight "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " No. Revisions "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " Actions ")
                  ])
                ], -1)),
                _createElementVNode("tbody", _hoisted_21, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(vaccinationRecords.value, (record, index) => {
                    return (_openBlock(), _createElementBlock("tr", {
                      key: index,
                      class: "hover:bg-gray-50"
                    }, [
                      _createElementVNode("td", _hoisted_22, _toDisplayString(index + 1), 1),
                      _createElementVNode("td", _hoisted_23, _toDisplayString(record.comment || 'No comment'), 1),
                      _createElementVNode("td", _hoisted_24, _toDisplayString(record.vaccinated || 0), 1),
                      _createElementVNode("td", _hoisted_25, _toDisplayString(record.weight || 'N/A'), 1),
                      _createElementVNode("td", _hoisted_26, _toDisplayString(record.revisions?.length || 0), 1),
                      _createElementVNode("td", null, [
                        _createElementVNode("button", {
                          onClick: ($event: any) => (openRevisionsModal(record, index)),
                          class: "px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        }, " Add Revision ", 8, _hoisted_27)
                      ])
                    ]))
                  }), 128))
                ])
              ]))
        ])
      ])
    ]),
    _createVNode(VaccinationRevisionModal, {
      entry: selectedEntry.value,
      configKey: activeConfigKey.value,
      entryIndex: selectedEntryIndex.value,
      isOpen: vaccinationRevisionModal.value,
      onClose: _cache[0] || (_cache[0] = () => vaccinationRevisionModal.value = false)
    }, null, 8, ["entry", "configKey", "entryIndex", "isOpen"]),
    _createVNode(TaskCompletionBlockedModal, {
      show: completionBlockModal.value,
      reason: completionValidation.value.reason,
      onClose: _cache[1] || (_cache[1] = ($event: any) => (completionBlockModal.value = false))
    }, null, 8, ["show", "reason"]),
    _createVNode(FooterActions, null, {
      middle: _withCtx(() => [
        _createElementVNode("button", {
          onClick: goBack,
          class: "w-full"
        }, " Back ")
      ]),
      end: _withCtx(() => [
        _createElementVNode("div", _hoisted_28, [
          _createElementVNode("button", {
            onClick: handleFinishTask,
            disabled: isFinishing.value,
            class: _normalizeClass(["w-full", { 'opacity-50 cursor-not-allowed': isFinishing.value }])
          }, " Finish Task ", 10, _hoisted_29)
        ])
      ]),
      _: 1
    })
  ]))
}
}

})